/**
 * Step1_TaskDetails Component
 *
 * First step of the wizard - Task/Activity selection and basic details.
 * Allows selection from 44 workboat tasks to pre-fill hazards.
 *
 * @module components/Step1_TaskDetails
 */

import React, { useState, useMemo } from 'react';
import {
  FileText,
  Search,
  X,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  Anchor,
  Settings,
  Navigation,
  Link,
  Users,
  AlertTriangle,
  Info,
  Ship       // FT-116: Vessel icon for multi-vessel picker
} from 'lucide-react';
import Dropdown from '../../../components/common/Dropdown';
import { ASSESSMENT_TYPES } from '../config/moduleConfig';
import { workboatTasks, TASK_CATEGORIES, getTasksByCategory } from '../data/workboatTasks';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useVessel } from '../../../contexts/VesselContext';  // FT-116 Phase 4

/**
 * Category icons mapping
 */
const CATEGORY_ICONS = {
  [TASK_CATEGORIES.DECK_OPERATIONS]: Anchor,
  [TASK_CATEGORIES.ENGINEERING_OPERATIONS]: Settings,
  [TASK_CATEGORIES.NAVIGATION_BRIDGE]: Navigation,
  [TASK_CATEGORIES.MOORING_ANCHORING]: Link,
  [TASK_CATEGORIES.CREW_WELFARE]: Users,
  [TASK_CATEGORIES.EMERGENCY_RESPONSE]: AlertTriangle
};

/**
 * Step1_TaskDetails Component
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.setFormData - Function to update form data
 * @param {Object} props.errors - Validation errors
 */
export const Step1_TaskDetails = ({ formData, setFormData, errors = {} }) => {
  const flags = useFeatureFlags();
  const [showTaskLibrary, setShowTaskLibrary] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showProcedureNotes, setShowProcedureNotes] = useState(!!formData.procedureNotes);

  // FT-116 Phase 4: Get vessels for multi-vessel picker
  const { vessels, currentVesselId, isMultiVessel } = useVessel();

  // Filter tasks based on search and category
  const filteredTasks = useMemo(() => {
    let tasks = workboatTasks;

    if (selectedCategory) {
      tasks = tasks.filter(t => t.category === selectedCategory);
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      tasks = tasks.filter(t =>
        t.title.toLowerCase().includes(lower) ||
        t.description.toLowerCase().includes(lower)
      );
    }

    return tasks;
  }, [searchTerm, selectedCategory]);

  // Group tasks by category for display
  const tasksByCategory = useMemo(() => {
    const grouped = {};
    filteredTasks.forEach(task => {
      if (!grouped[task.category]) {
        grouped[task.category] = [];
      }
      grouped[task.category].push(task);
    });
    return grouped;
  }, [filteredTasks]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTaskSelect = (task) => {
    setFormData(prev => ({
      ...prev,
      title: task.title,
      description: task.description,
      selectedTask: task,
      // Pre-fill hazards from the task
      hazards: task.commonHazards.map((hazard, index) => ({
        id: `HAZ-${Date.now()}-${index}`,
        hazardDescription: hazard,
        whoAffected: '',
        initialLikelihood: 3,
        initialSeverity: 3,
        residualLikelihood: 2,
        residualSeverity: 2,
        existingControls: task.suggestedControls[index] || '',
        additionalControls: '',
        controlHierarchyLevel: 'administrative',
        actionRequired: '',
        actionOwner: '',
        actionDeadline: ''
      }))
    }));
    setShowTaskLibrary(false);
  };

  const clearSelectedTask = () => {
    setFormData(prev => ({
      ...prev,
      selectedTask: null,
      hazards: []
    }));
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assessment Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title || ''}
          onChange={e => handleFieldChange('title', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
            errors.title ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="e.g., Deck Cargo Handling Operations"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title}</p>
        )}
      </div>

      {/* Type Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assessment Type <span className="text-red-500">*</span>
        </label>
        <Dropdown
          value={formData.type || 'generic'}
          onChange={(value) => handleFieldChange('type', value)}
          options={[
            { value: 'generic', label: 'Generic (Routine Operations)' },
            { value: 'task_specific', label: 'Task-Specific' },
            ...(flags.isComprehensiveTier ? [
              { value: 'dynamic', label: 'Dynamic (Real-time)' },
              { value: 'permit_to_work', label: 'Permit-to-Work' }
            ] : [])
          ]}
          width="w-full"
        />
        <p className="mt-1 text-sm text-gray-500">
          {ASSESSMENT_TYPES[formData.type]?.description || ASSESSMENT_TYPES.generic.description}
        </p>
      </div>

      {/* FT-116 REWRITE: Applicable Vessels (always fleet-scoped) */}
      {isMultiVessel && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Applicable Vessels
          </label>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600 mb-3 flex items-center gap-2">
              <Info className="h-4 w-4 flex-shrink-0" />
              <span>Select which vessels this RA applies to:</span>
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {vessels.map(vessel => {
                // FT-116: Use assignedVessels array, default to current vessel if empty
                const assignedVessels = formData.assignedVessels?.length > 0
                  ? formData.assignedVessels
                  : [currentVesselId];
                const isSelected = assignedVessels.includes(vessel.id);
                return (
                  <label
                    key={vessel.id}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-cyan-100 border border-cyan-300'
                        : 'bg-white border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const currentSelection = formData.assignedVessels?.length > 0
                          ? [...formData.assignedVessels]
                          : [currentVesselId];
                        let newVessels;
                        if (e.target.checked) {
                          newVessels = [...new Set([...currentSelection, vessel.id])];
                        } else {
                          newVessels = currentSelection.filter(id => id !== vessel.id);
                          // Ensure at least one vessel is selected
                          if (newVessels.length === 0) {
                            newVessels = [currentVesselId];
                          }
                        }
                        handleFieldChange('assignedVessels', newVessels);
                      }}
                      className="h-4 w-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                    />
                    <Ship className={`h-4 w-4 ${isSelected ? 'text-cyan-600' : 'text-gray-400'}`} />
                    <span className={`text-sm ${isSelected ? 'text-cyan-900 font-medium' : 'text-gray-700'}`}>
                      {vessel.name}
                      {vessel.id === currentVesselId && (
                        <span className="ml-2 text-xs text-gray-500">(current)</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {(formData.assignedVessels?.length || 1) > 1 && (
              <p className="mt-2 text-xs text-cyan-600">
                ✓ This RA will appear on {formData.assignedVessels?.length || 1} vessels
              </p>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description || ''}
          onChange={e => handleFieldChange('description', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="Briefly describe the activity or task being assessed..."
        />
      </div>

      {/* Workboat Task Selector */}
      <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50 p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-cyan-100 rounded-lg">
            <Anchor className="h-5 w-5 text-cyan-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">Quick Start: Workboat Tasks</h3>
            <p className="text-sm text-gray-600 mt-1">
              Select from 44 pre-defined WBC3 workboat tasks to auto-populate hazards and suggested controls.
            </p>

            {formData.selectedTask ? (
              <div className="mt-3 p-3 bg-white rounded-lg border border-cyan-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{formData.selectedTask.title}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.selectedTask.commonHazards.length} hazards will be pre-filled
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.selectedTask.wbc3Reference}
                    </p>
                  </div>
                  <button
                    onClick={clearSelectedTask}
                    className="p-1 text-gray-400 hover:text-red-500"
                    title="Remove selection"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowTaskLibrary(true)}
                className="mt-3 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Browse Workboat Tasks
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Location & Department */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <input
            type="text"
            value={formData.location || ''}
            onChange={e => handleFieldChange('location', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            placeholder="e.g., Main Deck, Engine Room"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Department
          </label>
          <input
            type="text"
            value={formData.department || ''}
            onChange={e => handleFieldChange('department', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            placeholder="e.g., Deck Operations, Engineering"
          />
        </div>
      </div>

      {/* Optional: Related Procedures */}
      <div className="border-t border-gray-200 pt-6">
        <button
          type="button"
          onClick={() => setShowProcedureNotes(!showProcedureNotes)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${showProcedureNotes ? 'rotate-180' : ''}`} />
          Related Procedures (Optional)
        </button>

        {showProcedureNotes && (
          <div className="mt-3">
            <textarea
              name="procedureNotes"
              value={formData.procedureNotes || ''}
              onChange={e => handleFieldChange('procedureNotes', e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Optionally reference related documents, checklists, or SMS sections. E.g., 'See Bridge Poster 3' or 'Company SMS Chapter 5.3'"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">
              {(formData.procedureNotes || '').length}/500 characters
            </p>
          </div>
        )}
      </div>

      {/* Task Library Modal */}
      {showTaskLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-slate-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Anchor className="h-6 w-6 text-white" />
                <h2 className="text-xl font-semibold text-white">Workboat Task Library</h2>
              </div>
              <button
                onClick={() => setShowTaskLibrary(false)}
                className="p-2 text-white hover:bg-slate-500 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search and Filter */}
            <div className="px-6 py-4 border-b border-gray-200 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    !selectedCategory
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All ({workboatTasks.length})
                </button>
                {Object.values(TASK_CATEGORIES).map(category => {
                  const count = workboatTasks.filter(t => t.category === category).length;
                  const Icon = CATEGORY_ICONS[category] || FileText;
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                        selectedCategory === category
                          ? 'bg-cyan-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Icon className="h-3 w-3" />
                      {category.split(' ')[0]} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Task List */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 240px)' }}>
              {Object.keys(tasksByCategory).length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No tasks found matching your search</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {Object.entries(tasksByCategory).map(([category, tasks]) => {
                    const Icon = CATEGORY_ICONS[category] || FileText;
                    return (
                      <div key={category}>
                        {/* Category Header */}
                        <div className="px-6 py-3 bg-gray-50 flex items-center gap-2">
                          <Icon className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-700">{category}</span>
                          <span className="text-sm text-gray-500">({tasks.length} tasks)</span>
                        </div>

                        {/* Tasks in category */}
                        {tasks.map(task => (
                          <button
                            key={task.id}
                            onClick={() => handleTaskSelect(task)}
                            className="w-full px-6 py-4 flex items-start gap-3 hover:bg-cyan-50 transition-colors text-left"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{task.title}</p>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {task.description}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  {task.commonHazards.length} hazards
                                </span>
                                <span className="text-xs text-gray-500">
                                  {task.wbc3Reference}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  <Info className="h-4 w-4 inline mr-1" />
                  Selecting a task will pre-fill hazards and controls
                </p>
                <button
                  onClick={() => setShowTaskLibrary(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Getting Started</h4>
            <p className="text-sm text-blue-800 mt-1">
              Enter a descriptive title for your risk assessment. You can either start from scratch
              or use one of the 44 pre-defined workboat tasks which will automatically populate
              common hazards and suggested controls.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1_TaskDetails;
